import { useState, useEffect } from "react";
import { Search, Plus, Check, AlertTriangle, Building2, ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { useTenant } from "@/hooks/useTenant";
import {
  findMatchingFunds,
  createMasterCatalogFund,
  validateCIK,
  validateLEI,
  FundMatchResult,
  FundMatchCandidate,
  FundInput,
} from "@/lib/fund-matching";

interface FundLookupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFundSelected: (instrumentId: string, instrumentName: string) => void;
  initialName?: string;
  initialIssuer?: string;
}

type Step = "search" | "results" | "create";

const FUND_STRATEGIES = [
  { value: "buyout", label: "Buyout" },
  { value: "growth_equity", label: "Growth Equity" },
  { value: "venture_capital", label: "Venture Capital" },
  { value: "real_estate", label: "Real Estate" },
  { value: "infrastructure", label: "Infrastructure" },
  { value: "credit", label: "Credit / Debt" },
  { value: "secondaries", label: "Secondaries" },
  { value: "fund_of_funds", label: "Fund of Funds" },
  { value: "hedge_fund", label: "Hedge Fund" },
  { value: "other", label: "Other" },
];

export function FundLookupDialog({
  open,
  onOpenChange,
  onFundSelected,
  initialName = "",
  initialIssuer = "",
}: FundLookupDialogProps) {
  const { orgId } = useTenant();
  const { toast } = useToast();

  const [step, setStep] = useState<Step>("search");
  const [isSearching, setIsSearching] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [matchResult, setMatchResult] = useState<FundMatchResult | null>(null);

  // Search form
  const [searchName, setSearchName] = useState(initialName);
  const [searchCik, setSearchCik] = useState("");
  const [searchLei, setSearchLei] = useState("");

  // Create form
  const [newFundName, setNewFundName] = useState("");
  const [newFundIssuer, setNewFundIssuer] = useState("");
  const [newFundStrategy, setNewFundStrategy] = useState("");
  const [newFundVintage, setNewFundVintage] = useState("");
  const [newFundCik, setNewFundCik] = useState("");
  const [newFundLei, setNewFundLei] = useState("");

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setStep("search");
      setSearchName(initialName);
      setSearchCik("");
      setSearchLei("");
      setMatchResult(null);
      setNewFundName(initialName);
      setNewFundIssuer(initialIssuer);
    }
  }, [open, initialName, initialIssuer]);

  const handleSearch = async () => {
    if (!orgId || !searchName.trim()) {
      toast({
        title: "Missing information",
        description: "Please enter a fund name to search",
        variant: "destructive",
      });
      return;
    }

    setIsSearching(true);
    try {
      const input: FundInput = {
        name: searchName.trim(),
        identifiers: {
          secCik: searchCik.trim() || undefined,
          lei: searchLei.trim() || undefined,
        },
      };

      const result = await findMatchingFunds(orgId, input);
      setMatchResult(result);
      setStep("results");

      // Pre-fill create form with search values
      setNewFundName(searchName.trim());
      setNewFundCik(searchCik.trim());
      setNewFundLei(searchLei.trim());
    } catch (error) {
      console.error("Search failed:", error);
      toast({
        title: "Search failed",
        description: "Failed to search for matching funds",
        variant: "destructive",
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectMatch = (candidate: FundMatchCandidate) => {
    onFundSelected(candidate.instrumentId, candidate.instrumentName);
    onOpenChange(false);
  };

  const handleCreateNew = async () => {
    if (!orgId || !newFundName.trim()) {
      toast({
        title: "Missing information",
        description: "Please enter a fund name",
        variant: "destructive",
      });
      return;
    }

    // Validate identifiers if provided
    if (newFundCik && !validateCIK(newFundCik)) {
      toast({
        title: "Invalid CIK",
        description: "SEC CIK should be a 10-digit number",
        variant: "destructive",
      });
      return;
    }

    if (newFundLei && !validateLEI(newFundLei)) {
      toast({
        title: "Invalid LEI",
        description: "LEI should be a 20-character alphanumeric code",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);
    try {
      const result = await createMasterCatalogFund(orgId, {
        name: newFundName.trim(),
        issuer: newFundIssuer.trim() || undefined,
        type: "private_fund",
        vintageYear: newFundVintage ? parseInt(newFundVintage) : undefined,
        identifiers: {
          secCik: newFundCik.trim() || undefined,
          lei: newFundLei.trim() || undefined,
        },
      });

      if (result) {
        toast({
          title: "Fund created",
          description: `${newFundName} has been added to the master catalog`,
        });
        onFundSelected(result.id, newFundName.trim());
        onOpenChange(false);
      } else {
        throw new Error("Failed to create fund");
      }
    } catch (error) {
      console.error("Create fund failed:", error);
      toast({
        title: "Creation failed",
        description: "Failed to create new fund",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const getMatchTypeBadge = (matchType: FundMatchCandidate["matchType"]) => {
    switch (matchType) {
      case "cik_exact":
      case "lei_exact":
        return <Badge variant="default" className="bg-green-600">Verified Match</Badge>;
      case "name_exact":
      case "alias_exact":
        return <Badge variant="default">Exact Match</Badge>;
      case "name_fuzzy":
      case "alias_fuzzy":
        return <Badge variant="secondary">Similar Name</Badge>;
      default:
        return null;
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return "text-green-600";
    if (confidence >= 0.7) return "text-yellow-600";
    return "text-muted-foreground";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {step === "search" && "Find or Add Fund"}
            {step === "results" && "Match Results"}
            {step === "create" && "Add New Fund"}
          </DialogTitle>
          <DialogDescription>
            {step === "search" &&
              "Search for an existing fund in the master catalog or add a new one"}
            {step === "results" &&
              "Review potential matches or create a new fund entry"}
            {step === "create" &&
              "Enter the fund details to add it to the master catalog"}
          </DialogDescription>
        </DialogHeader>

        {step === "search" && (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="fund-name">Fund Name *</Label>
              <Input
                id="fund-name"
                placeholder="e.g., ISquared Infrastructure II LP"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>

            <Separator />

            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">
                Optional: Add identifiers for better matching
              </Label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sec-cik">SEC CIK</Label>
                <Input
                  id="sec-cik"
                  placeholder="0001234567"
                  value={searchCik}
                  onChange={(e) => setSearchCik(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">10-digit SEC ID</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lei">LEI</Label>
                <Input
                  id="lei"
                  placeholder="5493001KJTIIGC8Y1R12"
                  value={searchLei}
                  onChange={(e) => setSearchLei(e.target.value.toUpperCase())}
                />
                <p className="text-xs text-muted-foreground">
                  Legal Entity Identifier
                </p>
              </div>
            </div>
          </div>
        )}

        {step === "results" && matchResult && (
          <div className="space-y-4 py-4">
            {matchResult.status === "no_match" ? (
              <div className="text-center py-6">
                <AlertTriangle className="h-12 w-12 mx-auto text-yellow-500 mb-3" />
                <p className="font-medium">No matching funds found</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Would you like to add "{searchName}" as a new fund?
                </p>
              </div>
            ) : (
              <>
                {matchResult.status === "high_confidence" && (
                  <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                    <div className="flex items-center gap-2 text-green-700">
                      <Check className="h-4 w-4" />
                      <span className="font-medium">
                        High confidence match found
                      </span>
                    </div>
                  </div>
                )}

                {matchResult.status === "medium_confidence" && (
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                    <div className="flex items-center gap-2 text-yellow-700">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="font-medium">
                        Please confirm the match
                      </span>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Potential Matches</Label>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {matchResult.candidates.map((candidate) => (
                      <button
                        key={candidate.instrumentId}
                        onClick={() => handleSelectMatch(candidate)}
                        className="w-full text-left rounded-lg border p-3 hover:bg-accent transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">
                              {candidate.instrumentName}
                            </p>
                            {candidate.issuer && (
                              <p className="text-sm text-muted-foreground">
                                GP: {candidate.issuer}
                              </p>
                            )}
                            {candidate.vintageYear && (
                              <p className="text-sm text-muted-foreground">
                                Vintage: {candidate.vintageYear}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            {getMatchTypeBadge(candidate.matchType)}
                            <span
                              className={`text-sm ${getConfidenceColor(
                                candidate.confidence
                              )}`}
                            >
                              {Math.round(candidate.confidence * 100)}% match
                            </span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {step === "create" && (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-fund-name">Fund Name *</Label>
              <Input
                id="new-fund-name"
                placeholder="e.g., ISquared Infrastructure II LP"
                value={newFundName}
                onChange={(e) => setNewFundName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-fund-issuer">GP / Issuer</Label>
              <Input
                id="new-fund-issuer"
                placeholder="e.g., ISquared Capital"
                value={newFundIssuer}
                onChange={(e) => setNewFundIssuer(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-fund-strategy">Strategy</Label>
                <Select
                  value={newFundStrategy}
                  onValueChange={setNewFundStrategy}
                >
                  <SelectTrigger id="new-fund-strategy">
                    <SelectValue placeholder="Select strategy" />
                  </SelectTrigger>
                  <SelectContent>
                    {FUND_STRATEGIES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-fund-vintage">Vintage Year</Label>
                <Input
                  id="new-fund-vintage"
                  type="number"
                  min="1990"
                  max="2030"
                  placeholder="e.g., 2024"
                  value={newFundVintage}
                  onChange={(e) => setNewFundVintage(e.target.value)}
                />
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">
                Optional: Add identifiers for deduplication
              </Label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-fund-cik">SEC CIK</Label>
                <div className="flex gap-2">
                  <Input
                    id="new-fund-cik"
                    placeholder="0001234567"
                    value={newFundCik}
                    onChange={(e) => setNewFundCik(e.target.value)}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    title="Look up on SEC EDGAR"
                    onClick={() =>
                      window.open(
                        `https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&company=${encodeURIComponent(
                          newFundName
                        )}&type=&dateb=&owner=include&count=40`,
                        "_blank"
                      )
                    }
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-fund-lei">LEI</Label>
                <div className="flex gap-2">
                  <Input
                    id="new-fund-lei"
                    placeholder="5493001KJTIIGC8Y1R12"
                    value={newFundLei}
                    onChange={(e) => setNewFundLei(e.target.value.toUpperCase())}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    title="Look up on GLEIF"
                    onClick={() =>
                      window.open(
                        `https://search.gleif.org/#/search/simpleSearch=${encodeURIComponent(
                          newFundName
                        )}`,
                        "_blank"
                      )
                    }
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {step === "search" && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleSearch} disabled={isSearching}>
                <Search className="h-4 w-4" />
                {isSearching ? "Searching..." : "Search"}
              </Button>
            </>
          )}

          {step === "results" && (
            <>
              <Button variant="outline" onClick={() => setStep("search")}>
                Back to Search
              </Button>
              <Button variant="outline" onClick={() => setStep("create")}>
                <Plus className="h-4 w-4" />
                Create New Fund
              </Button>
            </>
          )}

          {step === "create" && (
            <>
              <Button
                variant="outline"
                onClick={() =>
                  matchResult ? setStep("results") : setStep("search")
                }
              >
                Back
              </Button>
              <Button onClick={handleCreateNew} disabled={isCreating}>
                {isCreating ? "Creating..." : "Add Fund to Catalog"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
